import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import {
    FileText, Package, DollarSign, AlertCircle,
    Clock, CheckCircle, TrendingUp
} from 'lucide-react';
import { PageHeader } from '../../components/layout';
import { Card, CardBody, Badge, Table } from '../../components/common';
import { vendorPortalApi } from '../../services/api';
import './VendorPortal.css';

export default function VendorDashboard() {
    const navigate = useNavigate();

    const { data: stats } = useQuery({
        queryKey: ['vendorPortal', 'stats'],
        queryFn: () => vendorPortalApi.getStats(),
        select: (res) => res.data?.data || {}
    });

    const { data: pendingRFQs } = useQuery({
        queryKey: ['vendorPortal', 'pending-rfqs'],
        queryFn: () => vendorPortalApi.getPendingRFQs(),
        select: (res) => res.data?.data || []
    });

    const { data: recentPOs } = useQuery({
        queryKey: ['vendorPortal', 'recent-pos'],
        queryFn: () => vendorPortalApi.getRecentPOs(),
        select: (res) => res.data?.data || []
    });

    const statCards = [
        { label: 'Pending RFQs', value: stats?.pendingRFQs || 0, icon: FileText, color: '#f59e0b' },
        { label: 'Active POs', value: stats?.activePOs || 0, icon: Package, color: '#2563eb' },
        { label: 'Pending Payments', value: stats?.pendingPayments || 0, icon: DollarSign, color: '#10b981' },
        { label: 'Total Revenue', value: `IDR ${(stats?.totalRevenue || 0).toLocaleString()}`, icon: TrendingUp, color: '#8b5cf6' }
    ];

    const rfqColumns = [
        { key: 'rfqNumber', title: 'RFQ Number', dataIndex: 'rfqNumber' },
        { key: 'title', title: 'Title', dataIndex: 'title' },
        {
            key: 'deadline', title: 'Deadline', dataIndex: 'deadline',
            render: (d) => new Date(d).toLocaleDateString()
        },
        {
            key: 'action', title: '', width: 100,
            render: (_, row) => (
                <button
                    className="vp-link-btn"
                    onClick={() => navigate(`/vendor-portal/rfq/${row.id}/respond`)}
                >
                    Respond
                </button>
            )
        }
    ];

    const poColumns = [
        { key: 'poNumber', title: 'PO Number', dataIndex: 'poNumber' },
        {
            key: 'amount', title: 'Amount', dataIndex: 'totalAmount',
            render: (v) => `IDR ${v?.toLocaleString()}`
        },
        {
            key: 'status', title: 'Status', dataIndex: 'status',
            render: (s) => <Badge variant={s === 'acknowledged' ? 'success' : 'warning'}>{s}</Badge>
        }
    ];

    return (
        <div className="vendor-dashboard">
            <PageHeader
                title="Vendor Portal"
                subtitle="Welcome back!"
                breadcrumbs={[{ label: 'Dashboard', path: '/vendor-portal' }]}
            />

            <div className="vp-stats-grid">
                {statCards.map((stat) => (
                    <Card key={stat.label} className="vp-stat-card">
                        <CardBody>
                            <div className="vp-stat-icon" style={{ backgroundColor: `${stat.color}15`, color: stat.color }}>
                                <stat.icon size={24} />
                            </div>
                            <div className="vp-stat-info">
                                <span className="vp-stat-label">{stat.label}</span>
                                <span className="vp-stat-value">{stat.value}</span>
                            </div>
                        </CardBody>
                    </Card>
                ))}
            </div>

            <div className="vp-grid">
                <Card>
                    <CardBody>
                        <h3>Pending RFQs</h3>
                        <Table
                            columns={rfqColumns}
                            data={pendingRFQs || []}
                            emptyText="No pending RFQs"
                            rowKey="id"
                        />
                    </CardBody>
                </Card>

                <Card>
                    <CardBody>
                        <h3>Recent Purchase Orders</h3>
                        <Table
                            columns={poColumns}
                            data={recentPOs || []}
                            emptyText="No recent POs"
                            rowKey="id"
                        />
                    </CardBody>
                </Card>
            </div>
        </div>
    );
}
